import { useDynamicContent } from '@/hooks/useDynamicContent';
import '@/styles/animations.css';

/**
 * Props for the ProfessionsShowcase component
 */
interface ProfessionsShowcaseProps {
  /** Optional CSS class name for the container */
  className?: string;
  /** Feature flag to enable/disable the widget (defaults to true) */
  enabled?: boolean;
}

/**
 * Profession category data structure
 */
interface Profession {
  id: string;
  title: string;
  description: string;
  count?: number;
}

/**
 * Default professions representing alumni diversity
 */
const defaultProfessions: Profession[] = [
  {
    id: '1',
    title: 'Healthcare',
    description: 'Doctors, nurses, pharmacists, and healthcare administrators making a difference in medical care.',
    count: 45,
  },
  {
    id: '2',
    title: 'Technology',
    description: 'Software engineers, data scientists, and tech leaders driving innovation worldwide.',
    count: 62,
  },
  {
    id: '3',
    title: 'Education',
    description: 'Teachers, professors, and education administrators shaping future generations.',
    count: 38,
  },
  {
    id: '4',
    title: 'Business',
    description: 'Entrepreneurs, executives, and consultants leading organizations across industries.',
    count: 55,
  },
  {
    id: '5',
    title: 'Government',
    description: 'Civil servants, administrators, and policy makers serving the public.',
    count: 28,
  },
  {
    id: '6',
    title: 'Arts & Media',
    description: 'Artists, journalists, filmmakers, and creative professionals telling stories.',
    count: 22,
  },
  {
    id: '7',
    title: 'Engineering',
    description: 'Mechanical, civil, and electrical engineers building the world around us.',
    count: 41,
  },
  {
    id: '8',
    title: 'Finance',
    description: 'Bankers, accountants, and financial advisors managing wealth and investments.',
    count: 35,
  },
];

/**
 * Get the icon for a profession based on its title
 */
function getProfessionIcon(title: string): React.ReactNode {
  const lowerTitle = title.toLowerCase();

  if (lowerTitle.includes('health') || lowerTitle.includes('doctor') || lowerTitle.includes('medical')) {
    return (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
      </svg>
    );
  }
  if (lowerTitle.includes('tech') || lowerTitle.includes('software') || lowerTitle.includes('it')) {
    return (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    );
  }
  if (lowerTitle.includes('education') || lowerTitle.includes('teacher') || lowerTitle.includes('professor')) {
    return (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    );
  }
  if (lowerTitle.includes('business') || lowerTitle.includes('entrepreneur') || lowerTitle.includes('executive')) {
    return (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    );
  }
  if (lowerTitle.includes('government') || lowerTitle.includes('civil') || lowerTitle.includes('public')) {
    return (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    );
  }
  if (lowerTitle.includes('art') || lowerTitle.includes('media') || lowerTitle.includes('creative')) {
    return (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m0 0V1m0 3H7m10 0v2M7 4v2m0 0h10M7 6v12a2 2 0 002 2h6a2 2 0 002-2V6" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    );
  }
  if (lowerTitle.includes('engineer') || lowerTitle.includes('mechanical') || lowerTitle.includes('civil')) {
    return (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    );
  }
  if (lowerTitle.includes('finance') || lowerTitle.includes('bank') || lowerTitle.includes('account')) {
    return (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    );
  }

  // Default icon - briefcase
  return (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}

/**
 * Individual profession card component
 */
interface ProfessionCardProps {
  profession: Profession;
  index: number;
}

function ProfessionCard({ profession, index }: ProfessionCardProps) {
  return (
    <div
      className="group relative overflow-hidden rounded-2xl transition-all duration-300 hover:scale-[1.02] opacity-0 animate-fade-in-up"
      style={{
        animationDelay: `${index * 100}ms`,
        animationFillMode: 'forwards',
        background: 'linear-gradient(135deg, rgba(45, 45, 74, 0.7) 0%, rgba(26, 26, 46, 0.7) 100%)',
        border: '1px solid rgba(233, 69, 96, 0.15)',
      }}
    >
      {/* Hover glow effect */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 50% 0%, rgba(233, 69, 96, 0.15) 0%, transparent 70%)',
        }}
      />

      <div className="relative p-6">
        {/* Icon and Count row */}
        <div className="flex items-start justify-between mb-4">
          {/* Icon */}
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center text-primary group-hover:scale-110 transition-transform"
            style={{
              background: 'linear-gradient(135deg, rgba(233, 69, 96, 0.2) 0%, rgba(233, 69, 96, 0.05) 100%)',
              border: '1px solid rgba(233, 69, 96, 0.25)',
            }}
          >
            {getProfessionIcon(profession.title)}
          </div>

          {/* Alumni count badge */}
          {profession.count && (
            <div className="px-3 py-1 rounded-full bg-primary/10 border border-primary/20">
              <span className="text-xs text-primary font-medium">{profession.count}+ Alumni</span>
            </div>
          )}
        </div>

        {/* Title */}
        <h3 className="text-xl font-display font-semibold text-white mb-3 group-hover:text-primary transition-colors">
          {profession.title}
        </h3>

        {/* Description */}
        <p className="text-white/60 text-sm leading-relaxed">
          {profession.description}
        </p>

        {/* Decorative corner */}
        <div
          className="absolute bottom-0 right-0 w-24 h-24 pointer-events-none opacity-50 group-hover:opacity-100 transition-opacity"
          style={{
            background: 'radial-gradient(circle at 100% 100%, rgba(233, 69, 96, 0.1) 0%, transparent 70%)',
          }}
        />
      </div>
    </div>
  );
}

/**
 * Loading skeleton for profession cards
 */
function ProfessionsLoadingSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 animate-pulse">
      {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
        <div
          key={i}
          className="rounded-2xl p-6"
          style={{
            background: 'linear-gradient(135deg, rgba(45, 45, 74, 0.4) 0%, rgba(26, 26, 46, 0.4) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.05)',
          }}
        >
          <div className="flex items-start justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-white/5" />
            <div className="w-20 h-6 rounded-full bg-white/5" />
          </div>
          <div className="h-6 w-3/4 bg-white/5 rounded mb-3" />
          <div className="space-y-2">
            <div className="h-4 w-full bg-white/5 rounded" />
            <div className="h-4 w-5/6 bg-white/5 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * ProfessionsShowcase Component
 *
 * Displays a grid of alumni profession categories to highlight the diverse
 * success stories of the 1996 batch. Can be toggled via the `enabled` prop.
 *
 * Features:
 * - Responsive grid layout (1-4 columns)
 * - Staggered entrance animations
 * - Hover effects with glow
 * - Icon-based visual indicators
 * - Alumni count badges
 * - Dynamic content from API with fallback to defaults
 * - Feature flag support for easy enable/disable
 *
 * @example
 * ```tsx
 * <ProfessionsShowcase />
 *
 * <ProfessionsShowcase enabled={false} />
 *
 * <ProfessionsShowcase className="my-8" />
 * ```
 */
export function ProfessionsShowcase({ className = '', enabled = true }: ProfessionsShowcaseProps) {
  // Return null if the widget is disabled
  if (!enabled) {
    return null;
  }

  // Fetch dynamic content with fallback to default professions
  const { data: professions, isLoading } = useDynamicContent<Profession[]>(
    'alumni_professions',
    defaultProfessions
  );

  return (
    <section className={`py-20 px-4 relative overflow-hidden ${className}`}>
      {/* Background gradient */}
      <div
        className="absolute inset-0 opacity-40 pointer-events-none"
        style={{
          background: 'linear-gradient(180deg, rgba(233, 69, 96, 0.03) 0%, transparent 30%, rgba(233, 69, 96, 0.05) 100%)',
        }}
      />

      <div className="container mx-auto max-w-7xl relative z-10">
        {/* Section header */}
        <div className="text-center mb-12 opacity-0 animate-fade-in-up" style={{ animationFillMode: 'forwards' }}>
          <div className="inline-block px-4 py-2 rounded-full bg-primary/20 border border-primary/30 mb-4">
            <span className="text-sm text-primary font-medium uppercase tracking-wider">
              Alumni Network
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
            Where Are They Now?
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto">
            Our batchmates have gone on to achieve remarkable success across diverse fields.
            Reconnect with professionals from every walk of life at Re:Nav-96.
          </p>
        </div>

        {/* Professions grid */}
        {isLoading ? (
          <ProfessionsLoadingSkeleton />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {professions.map((profession, index) => (
              <ProfessionCard key={profession.id} profession={profession} index={index} />
            ))}
          </div>
        )}

        {/* Call to action */}
        <div
          className="mt-12 text-center opacity-0 animate-fade-in-up"
          style={{ animationDelay: '900ms', animationFillMode: 'forwards' }}
        >
          <p className="text-white/50 text-sm mb-4">
            Connect with alumni in your field or explore new opportunities through our network.
          </p>
          <div className="inline-flex items-center gap-2 text-primary/80">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <span className="text-sm font-medium">Power up your network at Re:Nav-96</span>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ProfessionsShowcase;
